'use client';

import { Content } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { FC } from 'react';
import Image from 'next/image';
import { Bounded } from '@/components/common/Bounded';

/**
 * Props for `ComparisonWithFeatureDiagram`.
 */
export type ComparisonWithFeatureDiagramProps =
  SliceComponentProps<Content.ComparisonWithFeatureDiagramSlice>;

/**
 * Component for "ComparisonWithFeatureDiagram" Slices.
 */
const ComparisonWithFeatureDiagram: FC<ComparisonWithFeatureDiagramProps> = ({
  slice,
}) => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-gradient-to-b from-[#0e1010] to-[#0f423a] text-white py-10 text-center"
    >
      <Bounded className="relative mx-auto max-w-screen-xl px-4 py-20">
        <div className="max-w-6xl mx-auto px-5">
          <div className="mb-[60px] text-left">
            <div className="mb-6">
              <PrismicRichText
                field={slice.primary.heading}
                components={{
                  heading1: ({ children }) => (
                    <h1 className="text-2xl md:text-3xl lg:text-4xl font-medium leading-[1.2] m-0 p-0">
                      {children}
                    </h1>
                  ),
                }}
              />
            </div>
            <div className="text-[#c4c4c4] text-lg leading-7">
              <PrismicRichText field={slice.primary.intro_text} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-[100px]">
            {/* Column One */}
            <div className="p-8 rounded-2xl text-left bg-black/20">
              <h2 className="text-2xl font-semibold mb-6 text-[#05f273]">
                {slice.primary.columnonetitle}
              </h2>
              <ul className="list-none p-0">
                {slice.primary.columnonebulletpoints?.map((item, idx) =>
                  item.text &&
                  typeof item.text === 'string' &&
                  item.text.trim() !== '' ? (
                    <li
                      key={idx}
                      className="mb-4 last:mb-0 flex items-start gap-2"
                    >
                      <span>{item.text}</span>
                    </li>
                  ) : null,
                )}
              </ul>
            </div>

            {/* Column Two */}
            <div className="p-8 rounded-2xl text-left bg-white/10">
              <h2 className="text-2xl font-semibold mb-6 text-[#05f273]">
                {slice.primary.columntwotitle}
              </h2>
              <ul className="list-none p-0">
                {slice.primary.columntwobulletpoints?.map((item, idx) =>
                  item.text &&
                  typeof item.text === 'string' &&
                  item.text.trim() !== '' ? (
                    <li
                      key={idx}
                      className="mb-4 last:mb-0 flex items-start gap-2"
                    >
                      <span>{item.text}</span>
                    </li>
                  ) : null,
                )}
              </ul>
            </div>
          </div>

          {/* Centerpiece Icon */}
          {slice.primary.centerpiece_icon?.url && (
            <div className="flex justify-center items-center my-4">
              <div className="relative">
                <Image
                  src={slice.primary.centerpiece_icon.url}
                  alt={slice.primary.centerpiece_icon.alt || ''}
                  width={500}
                  height={500}
                  className="w-auto h-auto max-w-full max-h-[400px] object-contain mx-auto"
                />
              </div>
            </div>
          )}
        </div>
      </Bounded>
    </section>
  );
};

export default ComparisonWithFeatureDiagram;
